import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import AuthContext from '../contexts/AuthContext';
import { formatTime } from '../utils/timeUtils';

const containerStyle = {
  padding: '1.5rem',
  display: 'flex',
  justifyContent: 'center',
  backgroundColor: '#f8f9fa'
};

const cardStyle = {
  backgroundColor: '#fff',
  padding: '2.5rem',
  borderRadius: '16px',
  boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
  maxWidth: '700px',
  width: '100%',
  border: '1px solid #e9ecef',
  position: 'relative'
};

const audioStyle = {
  display: 'none' /* Hide the audio element completely */
};

const buttonStyle = {
  backgroundColor: '#17a2b8',
  color: '#fff',
  border: 'none',
  padding: '0.75rem 1.5rem',
  borderRadius: '8px',
  cursor: 'pointer',
  fontWeight: '600',
  transition: 'all 0.3s ease',
  boxShadow: '0 4px 12px rgba(23, 162, 184, 0.3)',
  fontSize: '16px'
};

const buttonHoverStyle = {
  backgroundColor: '#138496',
  boxShadow: '0 6px 16px rgba(23, 162, 184, 0.4)',
  transform: 'scale(1.05)'
};

const headerStyle = {
  textAlign: 'center',
  marginBottom: '2rem'
};

const progressStyle = {
  width: '100%',
  height: '8px',
  backgroundColor: '#e9ecef',
  borderRadius: '4px',
  margin: '1rem 0',
  overflow: 'hidden'
};

const progressBarStyle = {
  height: '100%',
  background: 'linear-gradient(45deg, #17a2b8, #138496)',
  borderRadius: '4px',
  transition: 'width 0.3s ease'
};

const TestListenOnly = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const audioRef = useRef(null);
  
  const [audioUrl, setAudioUrl] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [audioDuration, setAudioDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testType, setTestType] = useState('');
  const [canListen, setCanListen] = useState(true);
  const [progress, setProgress] = useState(0);
  const [dictationWpm, setDictationWpm] = useState(0);
  const [selectedSpeed, setSelectedSpeed] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [source, setSource] = useState('listen-only');

  // Load saved speed from localStorage
  useEffect(() => {
    const savedSpeed = localStorage.getItem('dictationSpeed');
    if (savedSpeed) {
      setSelectedSpeed(parseInt(savedSpeed));
    }
  }, []);

  // Fetch test metadata and audio
  useEffect(() => {
    const fetchTest = async () => {
      try {
        const { data } = await api.get(`/tests/${id}`);
        
        if (data.testType === 'premium' && (!user || !user.isPremium)) {
          setCanListen(false);
        } else {
          setCanListen(true);
        }

        setAudioUrl(`data:${data.contentType};base64,${data.audio}`);
        setName(data.name);
        setCategory(data.category);
        setAudioDuration(data.audioDuration || 0);
        setTestType(data.testType);
        setDictationWpm(data.dictationWpm || 0);
      } catch (err) {
        console.error(err);
        setError('Failed to fetch test audio');
      } finally {
        setLoading(false);
      }
    };
    fetchTest();
  }, [id, user]);

  // Set up audio event listeners
  useEffect(() => {
    if (!audioRef.current || !audioUrl) return;

    const audio = audioRef.current;
    
    const handleTimeUpdate = () => {
      if (audio.duration) {
        const currentProgress = (audio.currentTime / audio.duration) * 100;
        setProgress(currentProgress);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      // Redirect back to test list page after audio completes
      navigate('/tests');
    };

    const handleLoadedMetadata = () => {
      // Reset progress when audio is loaded
      setProgress(0);
      // Set playback rate based on selected speed
      const playbackRate = 1 + (selectedSpeed / 100);
      audio.playbackRate = playbackRate;
      setIsPlaying(true);
    };

    const handlePlay = () => {
      setIsPlaying(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    // Set the source
    audio.src = audioUrl;
    
    // Ensure playback rate is set even if metadata is already loaded
    const playbackRate = 1 + (selectedSpeed / 100);
    audio.playbackRate = playbackRate;

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [audioUrl, navigate, selectedSpeed]);

  const handleBack = () => {
    navigate('/tests');
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getEffectiveSpeed = () => {
    return dictationWpm + selectedSpeed;
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #17a2b8',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          animation: 'spin 1s linear infinite',
          margin: '2rem auto'
        }}></div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            background: '#fff5f5',
            border: '1px solid #fed7d7',
            borderRadius: '12px',
            color: '#e53e3e'
          }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Error Loading Audio</h3>
            <p>{error}</p>
            <button 
              onClick={handleBack}
              style={{
                ...buttonStyle,
                marginTop: '1rem',
                backgroundColor: '#6c757d'
              }}
            >
              Back to Tests
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!canListen) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{
            textAlign: 'center',
            padding: '2rem',
            background: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '12px',
            color: '#856404'
          }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Premium Test</h3>
            <p>This is a premium test. Please upgrade to a premium subscription to listen to the audio.</p>
            <button 
              onClick={handleBack}
              style={{
                ...buttonStyle,
                marginTop: '1rem',
                backgroundColor: '#6c757d'
              }}
            >
              Back to Tests
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={headerStyle}>
          <h2 style={{ color: '#2c3e50', marginBottom: '0.5rem' }}>{name}</h2>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', marginBottom: '1rem' }}>
            {testType === 'premium' && (
              <span style={{ 
                backgroundColor: '#8e44ad', 
                color: '#fff', 
                padding: '0.5rem 1rem', 
                borderRadius: '20px', 
                fontSize: '12px', 
                fontWeight: 'bold' 
              }}>PREMIUM</span>
            )}
            {testType === 'simple' && (
              <span style={{ 
                backgroundColor: '#28a745', 
                color: '#fff', 
                padding: '0.5rem 1rem', 
                borderRadius: '20px', 
                fontSize: '12px', 
                fontWeight: 'bold' 
              }}>FREE</span>
            )}
            {testType === 'go-live' && (
              <span style={{ 
                backgroundColor: '#e74c3c', 
                color: '#fff', 
                padding: '0.5rem 1rem', 
                borderRadius: '20px', 
                fontSize: '12px', 
                fontWeight: 'bold' 
              }}>LIVE</span>
            )}
          </div>
          <p style={{ color: '#6c757d', fontSize: '14px' }}>
            Category: {category.toUpperCase()} | Duration: {formatTime(audioDuration)}
          </p>
          <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem' }}>
            <span style={{ color: '#6c757d', fontSize: '14px' }}>
              Base Speed: {dictationWpm} WPM
            </span>
            <span style={{ color: selectedSpeed !== 0 ? '#28a745' : '#6c757d', fontSize: '14px', fontWeight: '600' }}>
              {selectedSpeed !== 0 ? `Playing at +${selectedSpeed}%` : 'Playing at normal speed'}
            </span>
          </div>
        </div>

        {/* Progress Bar */}
        <div style={progressStyle}>
          <div style={{ ...progressBarStyle, width: `${progress}%` }}></div>
        </div>

        {/* Hidden Auto-playing Audio Player */}
        <audio
          ref={audioRef}
          src={audioUrl}
          style={audioStyle}
          autoPlay
        />

        <div style={{ textAlign: 'center', marginTop: '2rem' }}>
          <button
            onClick={handleBack}
            style={{
              ...buttonStyle,
              backgroundColor: '#6c757d'
            }}
            onMouseEnter={(e) => Object.assign(e.target.style, buttonHoverStyle)}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#6c757d';
              e.target.style.boxShadow = '0 4px 12px rgba(108, 117, 125, 0.3)';
              e.target.style.transform = 'none';
            }}
          >
            ← Back to Tests
          </button>
        </div>

        <div style={{
          textAlign: 'center',
          marginTop: '1rem',
          padding: '1rem',
          background: '#e8f4f8',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#0c5460'
        }}>
          <strong>🎵 Now Playing:</strong> Audio at {getEffectiveSpeed()} WPM will automatically finish and redirect you back to the test page.
        </div>
      </div>
    </div>
  );
};

export default TestListenOnly;
